import React from "react";
import { Link } from "react-router-dom";
import { InlineMath } from "react-katex";

const CBSEPhysics10 = () => {
  return (
    <div style={{ maxWidth: "900px", margin: "40px auto", padding: "20px", fontFamily: "'Nunito', 'Poppins', sans-serif", lineHeight: "1.8", color: "#1e293b" }}>
      
      {/* Hero Section */}
      <div style={{ background: "linear-gradient(135deg, #1e3a5f, #2c5282)", borderRadius: 16, padding: "40px 30px", color: "white", marginBottom: 30, textAlign: "center" }}>
        <h1 style={{ fontSize: "32px", margin: "0 0 10px 0" }}>⚡ CBSE Class 10 Physics — Complete Guide</h1>
        <p style={{ fontSize: "18px", opacity: 0.9, margin: "0 0 20px 0" }}>Light, Electricity, Magnetism, and more — explained clearly with formulas, diagrams, and instant AI help.</p>
        <a href="https://wa.me/919731453435?text=Help%20me%20with%20Class%2010%20Physics" target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", padding: "14px 32px", background: "#25D366", color: "white", borderRadius: 50, textDecoration: "none", fontWeight: "bold", fontSize: "16px" }}>
          💬 Ask Eluvent on WhatsApp
        </a>
      </div>

      {/* Quick Navigation */}
      <div style={{ background: "#f1f5f9", borderRadius: 12, padding: "20px", marginBottom: 30 }}>
        <h2 style={{ margin: "0 0 12px 0", fontSize: "20px" }}>📑 Jump to Chapter</h2>
        <ul style={{ columns: 2, paddingLeft: 20, margin: 0 }}>
          <li><a href="#light" style={{ color: "#2c5282" }}>Light — Reflection & Refraction</a></li>
          <li><a href="#eye" style={{ color: "#2c5282" }}>Human Eye & Colorful World</a></li>
          <li><a href="#electricity" style={{ color: "#2c5282" }}>Electricity</a></li>
          <li><a href="#magnetism" style={{ color: "#2c5282" }}>Magnetic Effects of Current</a></li>
        </ul>
      </div>

      {/* Chapter 1: Light */}
      <div id="light" style={{ marginBottom: 40 }}>
        <h2 style={{ borderLeft: "4px solid #2c5282", paddingLeft: 12, fontSize: "24px" }}>1. Light — Reflection & Refraction</h2>
        <p>Light travels in straight lines. When it hits a surface, it either bounces back (<strong>reflection</strong>) or bends into another medium (<strong>refraction</strong>).</p>
        
        <h3>🔑 Key Formulas</h3>
        <div style={{ background: "#f8fafc", padding: "16px", borderRadius: 8, marginBottom: 16 }}>
          <p><strong>Mirror Formula:</strong> <InlineMath math="\frac{1}{f} = \frac{1}{v} + \frac{1}{u}" /></p>
          <p><strong>Magnification:</strong> <InlineMath math="m = -\frac{v}{u} = \frac{h'}{h}" /></p>
          <p><strong>Snell's Law:</strong> <InlineMath math="n_1 \sin i = n_2 \sin r" /></p>
          <p><strong>Refractive Index:</strong> <InlineMath math="n = \frac{c}{v}" /></p>
        </div>

        <a href="https://wa.me/919731453435?text=Explain%20light%20reflection%20and%20refraction%20class%2010" target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", padding: "10px 20px", background: "#25D366", color: "white", borderRadius: 20, textDecoration: "none", fontWeight: "bold", fontSize: "14px", marginTop: 10 }}>
          💬 Stuck on Light? Ask Eluvent →
        </a>
      </div>

      {/* Chapter 2: Human Eye */}
      <div id="eye" style={{ marginBottom: 40 }}>
        <h2 style={{ borderLeft: "4px solid #2c5282", paddingLeft: 12, fontSize: "24px" }}>2. Human Eye & Colorful World</h2>
        <p>The human eye works like a camera. Light enters through the cornea, passes through the lens, and forms an image on the retina.</p>
        
        <h3>🔑 Key Concepts</h3>
        <ul>
          <li><strong>Myopia (Nearsightedness):</strong> Corrected by concave lens</li>
          <li><strong>Hypermetropia (Farsightedness):</strong> Corrected by convex lens</li>
          <li><strong>Dispersion:</strong> White light splits into 7 colors through a prism</li>
          <li><strong>Atmospheric Refraction:</strong> Twinkling of stars, early sunrise/sunset</li>
        </ul>

        <a href="https://wa.me/919731453435?text=Explain%20human%20eye%20class%2010%20physics" target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", padding: "10px 20px", background: "#25D366", color: "white", borderRadius: 20, textDecoration: "none", fontWeight: "bold", fontSize: "14px", marginTop: 10 }}>
          💬 Confused about Myopia? Ask Eluvent →
        </a>
      </div>

      {/* Chapter 3: Electricity */}
      <div id="electricity" style={{ marginBottom: 40 }}>
        <h2 style={{ borderLeft: "4px solid #2c5282", paddingLeft: 12, fontSize: "24px" }}>3. Electricity ⚡</h2>
        <p>Electric current is the flow of electric charge. This chapter covers circuits, resistance, and power calculations.</p>
        
        <h3>🔑 Key Formulas</h3>
        <div style={{ background: "#f8fafc", padding: "16px", borderRadius: 8, marginBottom: 16 }}>
          <p><strong>Ohm's Law:</strong> <InlineMath math="V = IR" /></p>
          <p><strong>Power:</strong> <InlineMath math="P = VI = I^2R = \frac{V^2}{R}" /></p>
          <p><strong>Series Resistance:</strong> <InlineMath math="R_s = R_1 + R_2 + R_3" /></p>
          <p><strong>Parallel Resistance:</strong> <InlineMath math="\frac{1}{R_p} = \frac{1}{R_1} + \frac{1}{R_2} + \frac{1}{R_3}" /></p>
        </div>

        <a href="https://wa.me/919731453435?text=Help%20me%20solve%20electricity%20numericals%20class%2010" target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", padding: "10px 20px", background: "#25D366", color: "white", borderRadius: 20, textDecoration: "none", fontWeight: "bold", fontSize: "14px", marginTop: 10 }}>
          💬 Practice Numerical? Ask Eluvent →
        </a>
      </div>

      {/* Chapter 4: Magnetism */}
      <div id="magnetism" style={{ marginBottom: 40 }}>
        <h2 style={{ borderLeft: "4px solid #2c5282", paddingLeft: 12, fontSize: "24px" }}>4. Magnetic Effects of Current 🧲</h2>
        <p>A current-carrying conductor produces a magnetic field. This is the principle behind electromagnets and electric motors.</p>
        
        <h3>🔑 Key Concepts</h3>
        <ul>
          <li><strong>Right-Hand Thumb Rule:</strong> Direction of magnetic field around a straight conductor</li>
          <li><strong>Fleming's Left-Hand Rule:</strong> Direction of force on a conductor in a magnetic field</li>
          <li><strong>Electric Motor:</strong> Converts electrical energy to mechanical energy</li>
          <li><strong>Electromagnetic Induction:</strong> Changing magnetic field induces current (Generator)</li>
        </ul>

        <a href="https://wa.me/919731453435?text=Explain%20magnetic%20effects%20of%20current%20class%2010" target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", padding: "10px 20px", background: "#25D366", color: "white", borderRadius: 20, textDecoration: "none", fontWeight: "bold", fontSize: "14px", marginTop: 10 }}>
          💬 Fleming's Rule confusing? Ask Eluvent →
        </a>
      </div>

      {/* FAQ Section */}
      <div style={{ marginBottom: 40 }}>
        <h2 style={{ borderLeft: "4px solid #2c5282", paddingLeft: 12, fontSize: "24px" }}>❓ Frequently Asked Questions</h2>
        
        <div itemScope itemType="https://schema.org/FAQPage">
          <div itemScope itemProp="mainEntity" itemType="https://schema.org/Question" style={{ marginBottom: 16, padding: "16px", background: "#f8fafc", borderRadius: 8 }}>
            <h3 itemProp="name" style={{ fontSize: "16px", margin: "0 0 8px 0" }}>Q: Is Class 10 Physics hard?</h3>
            <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
              <p itemProp="text" style={{ margin: 0 }}>Not at all! With clear concepts, formula practice, and numerical solving, Class 10 Physics becomes easy. Eluvent's AI tutor helps you understand every topic step-by-step on WhatsApp.</p>
            </div>
          </div>

          <div itemScope itemProp="mainEntity" itemType="https://schema.org/Question" style={{ marginBottom: 16, padding: "16px", background: "#f8fafc", borderRadius: 8 }}>
            <h3 itemProp="name" style={{ fontSize: "16px", margin: "0 0 8px 0" }}>Q: How to score 90+ in CBSE Class 10 Physics?</h3>
            <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
              <p itemProp="text" style={{ margin: 0 }}>Master NCERT concepts, practice all numericals, draw ray diagrams neatly, and use Eluvent for instant doubt-solving. Consistent revision is key!</p>
            </div>
          </div>
        </div>
      </div>

      {/* Interlinking */}
      <div style={{ background: "#e0f2fe", borderRadius: 12, padding: "20px", marginBottom: 30, textAlign: "center" }}>
        <h3 style={{ margin: "0 0 12px 0" }}>📚 Explore Other Subjects</h3>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
          <Link to="/cbse-class-10-chemistry" style={{ padding: "10px 18px", background: "#2c5282", color: "white", borderRadius: 20, textDecoration: "none", fontSize: "14px" }}>🧪 Chemistry</Link>
          <Link to="/cbse-class-10-maths" style={{ padding: "10px 18px", background: "#2c5282", color: "white", borderRadius: 20, textDecoration: "none", fontSize: "14px" }}>📐 Mathematics</Link>
          <Link to="/cbse-class-10-biology" style={{ padding: "10px 18px", background: "#2c5282", color: "white", borderRadius: 20, textDecoration: "none", fontSize: "14px" }}>🧬 Biology</Link>
        </div>
      </div>

      {/* Final CTA */}
      <div style={{ textAlign: "center", padding: "30px", background: "linear-gradient(135deg, #1e3a5f, #2c5282)", borderRadius: 16, color: "white" }}>
        <h2 style={{ margin: "0 0 10px 0" }}>🚀 Ready to Ace Physics?</h2>
        <p style={{ margin: "0 0 20px 0", fontSize: "16px" }}>Ask any question on WhatsApp and get instant, clear answers from Eluvent — your AI tutor.</p>
        <a href="https://wa.me/919731453435?text=Help%20me%20with%20CBSE%20Class%2010%20Physics" target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", padding: "14px 32px", background: "#25D366", color: "white", borderRadius: 50, textDecoration: "none", fontWeight: "bold", fontSize: "16px" }}>
          💬 Start Learning on WhatsApp
        </a>
      </div>

    </div>
  );
};

export default CBSEPhysics10;